import { useQuery } from '@tanstack/react-query';
import { Amount } from '@ui/components/Amount/Amount';
import { HLine } from '@ui/components/Line';
import { TextTooltip } from '@ui/components/TextTooltip';
import { Interval } from '@ui/constants/poll';
import { sLVMON } from '@ui/constants/tokens';
import { usePayTokenBalance } from '@ui/hooks/usePayTokenBalance';
import { Button } from '@ui/lib/park/ui/button';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { slvmonQuery } from '@ui/services/backend/hooks/slvmon/slvmonQuery';
import SLVMONABI from '@ui/services/contract/abis/SLVMON';
import BigNumber from 'bignumber.js';
import { useContext, useMemo } from 'react';
import { Box, HStack, styled,VStack } from 'styled-system/jsx';
import { Address, formatUnits, parseUnits } from 'viem';
import { useAccount,useChainId, useReadContract } from 'wagmi';

import { ExchangeContext } from '../Context';
import { StakeShareOverlay } from './StakeShareOverlay';

export const MyPosition = () => {
  const chainId = useChainId();
  const { address } = useAccount();
  const sLVMONToken = sLVMON(chainId);

  const { outputAmount, outputToken } = useContext(ExchangeContext);
  const userShareBalance = usePayTokenBalance(sLVMONToken, Interval.FAST);

  const { data: portfolio } = useQuery({
    queryKey: slvmonQuery.getUserPortfolioKey({ userAddress: address || '' }),
    queryFn: slvmonQuery.getUserPortfolioFn({ userAddress: address || '' }),
    enabled: !!address,
    refetchInterval: Interval.FAST,
  });

  const { data: oneShareInAssets } = useReadContract({
    address: sLVMONToken?.address as Address,
    abi: SLVMONABI,
    functionName: 'convertToAssets',
    args: [parseUnits('1', sLVMONToken?.decimals)],
    query: {
      enabled: !!sLVMONToken?.address,
    },
  });

  const rate = useMemo(() => {
    if (!oneShareInAssets) return null;
    
return new BigNumber(formatUnits(oneShareInAssets, sLVMONToken?.decimals || 18));
  }, [oneShareInAssets, sLVMONToken]);

  const stakedAmount = useMemo(() => {
    // Use on-chain sLVMON balance + convert rate for immediate updates after stake/unstake.
    if (!userShareBalance || !rate) return null;
    
return userShareBalance.times(rate);
  }, [userShareBalance, rate]);

  const rewards = useMemo(() => {
    if (!portfolio || !rate) return null;
    const shares = new BigNumber(portfolio.shares).shiftedBy(-(sLVMONToken?.decimals || 18));
    const principalVal = new BigNumber(portfolio.principal).shiftedBy(-(sLVMONToken?.decimals || 18));
    const currentValue = shares.times(rate);
    const result = currentValue.minus(principalVal);
    
return result.lt(0) ? new BigNumber(0) : result;
  }, [portfolio, rate, sLVMONToken]);

  const principalStakedAmount = useMemo(() => {
    if (!stakedAmount || !rewards) return stakedAmount;
    const result = stakedAmount.minus(rewards);
    
return result.lt(0) ? new BigNumber(0) : result;
  }, [stakedAmount, rewards]);

  const totalRewards = useMemo(() => {
    if (!portfolio || !rewards) return null;
    const cumulativePnl = new BigNumber(portfolio.cumulativeRealizedPnl).shiftedBy(-(sLVMONToken?.decimals || 18));
    
return rewards.plus(cumulativePnl);
  }, [portfolio, rewards, sLVMONToken]);

  const expectedIncrease = useMemo(() => {
    if (!outputAmount || !rate || outputToken?.symbol !== 'sLVMON') return null;
    const amount = new BigNumber(outputAmount);
    if (amount.isNaN() || amount.isZero()) return null;
    
return amount.times(rate);
  }, [outputAmount, rate, outputToken]);

  return (
    <Box width="full" mb={4}>
      <HLine />
      <HStack justifyContent="space-between" alignItems="center" mt={3} mb={2}>
        <Text color="greyWhite.grey" fontSize={14}>
          My Position
        </Text>
        {address && (
          <StakeShareOverlay stakedAmount={stakedAmount}>
            <Button
              variant="text"
              disabled={!stakedAmount || stakedAmount.lte(0)}
              color="rgba(255,255,255,0.6)"
              _hover={{ color: 'white' }}
            >
              <HStack gap={1}>
                <Icon boxSize="14px">
                  <styled.img
                    src="/images/share.svg"
                    width="100%"
                    height="100%"
                  />
                </Icon>
                <Text fontSize={13}>Share</Text>
              </HStack>
            </Button>
          </StakeShareOverlay>
        )}
      </HStack>
      <HStack justifyContent="space-between" mb={2}>
        <HStack gap={1}>
          <Text color="rgba(255,255,255,0.5)" fontWeight="normal" fontSize={14}>
            Total Staked:
          </Text>
          <TextTooltip
            content={
              <VStack alignItems="stretch" gap={1}>
                <Text>
                  Total staked includes your principal and currently accrued rewards.
                </Text>
                <Text color="rgba(255,255,255,0.65)">
                  Staked LVMON auto-compounds.
                </Text>
                <HStack justifyContent="space-between" gap={3}>
                  <Text color="rgba(255,255,255,0.65)">Principal:</Text>
                  <Amount
                    value={principalStakedAmount}
                    color="white"
                    fontWeight="medium"
                    opts={{ displayDecimals: 2, autoKmb: false }}
                  />
                </HStack>
                <HStack justifyContent="space-between" gap={3}>
                  <Text color="rgba(255,255,255,0.65)">Rewards:</Text>
                  <Amount
                    value={rewards}
                    color="#D4F82D"
                    fontWeight="medium"
                    opts={{ displayDecimals: 2, autoKmb: false }}
                  />
                </HStack>
              </VStack>
            }
            triggerCss={{
              textDecoration: 'none',
              cursor: 'help',
            }}
            contentCss={{
              w: '240px',
              lineHeight: '18px',
              whiteSpace: 'normal',
            }}
          >
            <Text
              as="span"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              w="12px"
              h="12px"
              borderRadius="9999px"
              border="1px solid rgba(255,255,255,0.35)"
              color="rgba(255,255,255,0.55)"
              fontSize={9}
              lineHeight="9px"
            >
              ?
            </Text>
          </TextTooltip>
        </HStack>
        <HStack>
          <styled.img
            src="/tokens/LVMON.svg"
            width="16px"
            height="16px"
          />
          <HStack gap={1}>
            <Amount
              value={stakedAmount}
              color="white"
              fontSize={14}
              fontWeight="bold"
              opts={{ displayDecimals: 2, autoKmb: false }}
            />
            {expectedIncrease && (
              <Text color="#D4F82D" fontSize={14} fontWeight="bold">
                (+{expectedIncrease.toFormat(2)})
              </Text>
            )}
          </HStack>
        </HStack>
      </HStack>
      <HStack justifyContent="space-between" mb={2}>
        <Text color="rgba(255,255,255,0.5)" fontWeight="normal" fontSize={14}>
          Current Rewards:
        </Text>
        <HStack>
          <styled.img
            src="/tokens/LVMON.svg"
            width="16px"
            height="16px"
          />
          <Amount
            value={rewards}
            color="white"
            fontSize={14}
            fontWeight="bold"
            opts={{ displayDecimals: 2, autoKmb: false }}
          />
        </HStack>
      </HStack>
      <HStack justifyContent="space-between">
        <HStack gap={1}>
          <Text color="rgba(255,255,255,0.5)" fontWeight="normal" fontSize={14}>
            Cumulative Rewards:
          </Text>
          <TextTooltip
            content="Cumulative rewards are the total rewards you have earned historically, including currently accrued rewards."
            triggerCss={{
              textDecoration: 'none',
              cursor: 'help',
            }}
            contentCss={{
              w: '240px',
              lineHeight: '18px',
              whiteSpace: 'normal',
            }}
          >
            <Text
              as="span"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              w="12px"
              h="12px"
              borderRadius="9999px"
              border="1px solid rgba(255,255,255,0.35)"
              color="rgba(255,255,255,0.55)"
              fontSize={9}
              lineHeight="9px"
            >
              ?
            </Text>
          </TextTooltip>
        </HStack>
        <HStack>
          <styled.img
            src="/tokens/LVMON.svg"
            width="16px"
            height="16px"
          />
          <Amount
            value={totalRewards}
            color="white"
            fontSize={14}
            fontWeight="bold"
            opts={{ displayDecimals: 2, autoKmb: false }}
          />
        </HStack>
      </HStack>
    </Box>
  );
};
